import {useState, useEffect, useCallback, useMemo} from 'react'
import {motion, AnimatePresence} from 'framer-motion'
import {isValidDate, parseISODate} from '../../utils'
import {kegstarCollectionEmailAddress} from '../../utils/customerSupportEmailAddress'
import {isFeatureFlagEnabled} from '../../config/featureFlags'
import {
  isPastMaintenanceWindow,
  isWithinMaintenanceWindow,
  MAINTENANCE_WINDOW_END_TIME,
  MAINTENANCE_WINDOW_START_TIME,
} from '../../utils/maintenanceWindow'

import ChooseLocationPage from './ChooseLocationPage'
import CookiesConsent from '../CookiesConsent/CookiesConsent'
import EnterQuantitiesPage from './EnterQuantitiesPage'
import MaintenanceBanner from '../../components/MaintenanceBanner/MaintenanceBanner'
import Stepper from './Stepper'

import {
  LOCAL_STORAGE_KEY_CHOSEN_LOCATION,
  LOCAL_STORAGE_KEY_HAS_USER_CONSENTED_TO_COOKIES,
  LOCAL_STORAGE_KEY_LAST_SUBMISSION_DATE,
} from '../../constants'
import {FetchVenuesResponse as Address} from '../../services'
import {CurrentlyDisplayedPage} from '../../types'
import useLocalStorageState from '../../hooks/useLocalStorageState'

export default function EmptyKegReport() {
  const [hasUserConsentedToCookies, setHasUserConsentedToCookies] = useLocalStorageState<
    boolean | null
  >({
    key: LOCAL_STORAGE_KEY_HAS_USER_CONSENTED_TO_COOKIES,
    initialValue: null,
    version: '1',
  })
  const [chosenLocation, setChosenLocation] = useLocalStorageState<Address | null>({
    key: LOCAL_STORAGE_KEY_CHOSEN_LOCATION,
    initialValue: null,
    version: '1',
  })
  const [lastSubmissionDate, setLastSubmissionDate, resetLastSubmissionDate] =
    useLocalStorageState<Date | null>({
      key: LOCAL_STORAGE_KEY_LAST_SUBMISSION_DATE,
      initialValue: null,
      version: '1',
    })

  const [chosenLocationLocalState, setChosenLocationLocalState] = useState<Address | null>(null)

  // If chosenLocation is not null, we start on the Enter Quantities page.
  // If it is null, we start on the Choose Location page.
  const [currentlyDisplayedPage, setCurrentlyDisplayedPage] = useState<CurrentlyDisplayedPage>(
    chosenLocation ? CurrentlyDisplayedPage.EnterQuantities : CurrentlyDisplayedPage.ChooseLocation
  )

  const kegstarCollectionEmailInquirySubject = 'Enquiring the status of an existing booking.'
  // codeCommentsAutoFocusDistortsAnimation
  const [
    isLocationToQtysPageSwipeCurrentlyAnimating,
    setIsLocationToQtysPageSwipeCurrentlyAnimating,
  ] = useState<boolean>(false)

  useEffect(() => {
    // If the user had previously declined cookies, reset their decision
    // This will ensure that the cookies consent banner shows up again on initial load
    if (hasUserConsentedToCookies === false) {
      setHasUserConsentedToCookies(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // When the user chooses a location, assume that they always want to
  // go immediately to the "Enter Quantities" page.
  useEffect(() => {
    if (chosenLocation === null && chosenLocationLocalState === null) return
    setCurrentlyDisplayedPage(CurrentlyDisplayedPage.EnterQuantities)
  }, [chosenLocation, chosenLocationLocalState])

  // Our pages have a nice swipe/slide animation effect when the user switches
  // between the Choose Location page and the Enter Quantities page. These
  // motion props are shared between the two pages.
  const motionDivProps = useMemo(() => {
    return {
      transition: {
        x: {type: 'tween', duration: 0.15},
      },
      custom: currentlyDisplayedPage,
      variants: {
        enter: (currentlyDisplayedPage: CurrentlyDisplayedPage) => {
          return {
            x: currentlyDisplayedPage === CurrentlyDisplayedPage.EnterQuantities ? 350 : -350,
          }
        },
        center: {
          x: 0,
        },
        exit: (currentlyDisplayedPage: CurrentlyDisplayedPage) => {
          return {
            x: currentlyDisplayedPage === CurrentlyDisplayedPage.ChooseLocation ? 350 : -350,
          }
        },
      },
      initial: 'enter',
      animate: 'center',
      exit: 'exit',
    }
  }, [currentlyDisplayedPage])

  const validatedDate = useMemo(() => {
    if (!lastSubmissionDate) return null
    const d =
      typeof lastSubmissionDate === 'string' ? parseISODate(lastSubmissionDate) : lastSubmissionDate
    if (d && isValidDate(d)) {
      return d
    }
    return null
  }, [lastSubmissionDate])

  const chosenLocationComparable = useMemo(
    () => (chosenLocation ? JSON.stringify(chosenLocation) : null),
    [chosenLocation]
  )

  const dateTimeFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
      }),
    []
  )

  const setChosenLocationWithReset = useCallback(
    (address: Address) => {
      if (!hasUserConsentedToCookies) {
        setChosenLocationLocalState(address)
        return
      }

      const nextComparable = JSON.stringify(address)

      // If same location, no reset needed
      if (chosenLocationComparable === nextComparable) {
        setChosenLocation(address)
        return
      }

      setChosenLocation(address)
      resetLastSubmissionDate()

      if (chosenLocationLocalState) {
        setChosenLocationLocalState(null)
      }
    },
    [
      hasUserConsentedToCookies,
      chosenLocationComparable,
      chosenLocationLocalState,
      setChosenLocation,
      setChosenLocationLocalState,
      resetLastSubmissionDate,
    ]
  )

  const customerSupportMessage = (
    <>
      <p className="break-words text-xs font-medium text-slate-500 dark:text-slate-400 ml-6 mr-6 mt-2 border border-sky-300 dark:border-sky-700 p-2 rounded">
        If you have already submitted a request for collections and you are just enquiring or
        needing the current status of the existing booking, please contact{' '}
        <a
          href={`mailto:${kegstarCollectionEmailAddress(chosenLocation, chosenLocationLocalState)}?subject=${encodeURIComponent(kegstarCollectionEmailInquirySubject)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sky-500 dark:text-sky-400"
        >
          {kegstarCollectionEmailAddress(chosenLocation, chosenLocationLocalState)}
        </a>
        .
        {validatedDate ? (
          <span className="block text-center mt-2">
            Last Submitted on:&nbsp;
            {dateTimeFormatter.format(validatedDate)}
          </span>
        ) : null}
      </p>
    </>
  )

  const chooseLocationSubHeader = (
    <p className="text-center text-slate-500 dark:text-slate-400 text-sm px-8 pb-1">
      Search for your venue and select it to continue with your collection request.
    </p>
  )

  const effectiveChosenLocation = chosenLocation || chosenLocationLocalState
  const isMaintenanceModeEnabled = isFeatureFlagEnabled('MAINTENANCE_MODE')

  if (
    isMaintenanceModeEnabled &&
    isWithinMaintenanceWindow(MAINTENANCE_WINDOW_START_TIME, MAINTENANCE_WINDOW_END_TIME)
  ) {
    return (
      <>
        <MaintenanceBanner
          startDate={MAINTENANCE_WINDOW_START_TIME}
          endDate={MAINTENANCE_WINDOW_END_TIME}
          message="We are currently performing maintenance on the system. The system will be down during the following time period:"
        />
      </>
    )
  }

  return (
    <>
      {isMaintenanceModeEnabled &&
      !isPastMaintenanceWindow(MAINTENANCE_WINDOW_START_TIME, MAINTENANCE_WINDOW_END_TIME) ? (
        <MaintenanceBanner
          title="Scheduled Maintenance"
          startDate={MAINTENANCE_WINDOW_START_TIME}
          endDate={MAINTENANCE_WINDOW_END_TIME}
        />
      ) : null}
      <h1 className="text-center pt-2 text-lg font-kegstarHeading font-bold text-slate-800 dark:text-slate-100">
        Kegstar Collections
      </h1>
      {currentlyDisplayedPage === CurrentlyDisplayedPage.EnterQuantities
        ? customerSupportMessage
        : chooseLocationSubHeader}
      <div className="h-12 md:h-16 px-3 flex flex-col align-center justify-center">
        <Stepper
          currentlyDisplayedPage={currentlyDisplayedPage}
          setCurrentlyDisplayedPage={setCurrentlyDisplayedPage}
          chosenLocation={chosenLocation}
        />
      </div>
      {/* @ts-ignore */}
      <AnimatePresence
        initial={false}
        custom={currentlyDisplayedPage}
        // I'd love to use mode="sync" to make the transition faster and make
        // the slide animation more visually seamless, but I ran into this
        // problem: https://stackoverflow.com/q/71434254
        mode="wait"
      >
        {currentlyDisplayedPage === CurrentlyDisplayedPage.ChooseLocation ? (
          <motion.div
            key={currentlyDisplayedPage}
            onAnimationStart={() => {
              // codeCommentsAutoFocusDistortsAnimation
              setIsLocationToQtysPageSwipeCurrentlyAnimating(true)
            }}
            {...motionDivProps}
          >
            <ChooseLocationPage setChosenLocation={setChosenLocationWithReset} />
          </motion.div>
        ) : (
          <motion.div
            key={currentlyDisplayedPage}
            onAnimationComplete={() => {
              // codeCommentsAutoFocusDistortsAnimation
              setIsLocationToQtysPageSwipeCurrentlyAnimating(false)
            }}
            {...motionDivProps}
          >
            <EnterQuantitiesPage
              isLocationToQtysPageSwipeCurrentlyAnimating={
                isLocationToQtysPageSwipeCurrentlyAnimating
              }
              chosenLocation={effectiveChosenLocation}
              setLastSubmissionDate={setLastSubmissionDate}
              setCurrentlyDisplayedPage={setCurrentlyDisplayedPage}
              hasUserConsentedToCookies={hasUserConsentedToCookies}
            />
          </motion.div>
        )}
      </AnimatePresence>
      <CookiesConsent
        hasUserConsentedToCookies={hasUserConsentedToCookies}
        setHasUserConsentedToCookies={setHasUserConsentedToCookies}
      />
    </>
  )
}
